import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PortfolioService } from '../../portfolio';
import { SearchSymbolService } from '../../search/services/search-symbol.service';
import {
	BaseMessage,
	MessageTypes,
	TextMessageFilter,
	MarketMessage,
	MessageContentTypes,
	MofidMessagesInput
} from '../models/message.model';
import { MessageStore } from '../../../state/messages/message.store';
import { BaseMessageService } from './base-message.service';
import { changeMsgBodyStyle, typeExtractor } from './message.tools';
import { DeviceTypeEnum } from '../../account-activity';
import { HttpParams } from '@angular/common/http';
import { OrderStore } from '@easy-lib/state/orders';

@Injectable({
	providedIn: 'root'
})
export class MessageService extends BaseMessageService {
	public portfolioService: PortfolioService = inject(PortfolioService);
	public searchService: SearchSymbolService = inject(SearchSymbolService);
	public orderStore: OrderStore = inject(OrderStore);
	public messageStore: MessageStore = inject(MessageStore);
	get(): Observable<BaseMessage[]> {
		const from = new Date();
		from.setHours(0, 0, 0);
		const to = new Date();
		to.setHours(23, 59, 59);
		const filter: TextMessageFilter = {
			from,
			to
		};
		return this.httpService
			.post<BaseMessage[]>(`${this.mtsPath + this.apiUrls.easy}textmessage/user-messages`, { body: filter })
			.pipe(
				map((messages: MarketMessage[]) =>
					messages.map(msg => {
						const title = this.msgTitleFix(msg.title);
						return {
							...msg,
							title,
							contentType: typeExtractor(title)
						};
					})
				)
			);
	}
	getMessagesHistoryForIsin(isin: string): Observable<MarketMessage[]> {
		const from = new Date();
		from.setMonth(from.getMonth() - 1);
		from.setHours(0, 0, 0);
		const to = new Date();
		to.setHours(23, 59, 59);
		const filter: TextMessageFilter = {
			type: MessageTypes.MarketMessage,
			from,
			to,
			isin
		};
		return this.httpService
			.post<MarketMessage[]>(`${this.mtsPath + this.apiUrls.easy}textmessage/user-messages`, { body: filter })
			.pipe(
				map(messages => messages.filter(msg => !msg.body.startsWith('d'))),
				map(messages => {
					messages.forEach(m => {
						m.title = this.msgTitleFix(m.title);
						m.contentType = typeExtractor(m.title);
					});

					return messages;
				})
			);
	}
	getMofidMessage(input: MofidMessagesInput): Observable<BaseMessage[]> {
		let params = new HttpParams();
		params = params.append('pageSize', input.pageSize);
		params = params.append('pageNo', input.pageNo);
		if (input.symbolIsin) params = params.append('symbolIsin', input.symbolIsin);
		return this.httpService
			.get<BaseMessage[]>(`${this.mtsPath + this.apiUrls.easy}textmessage/${this.createMofidUrl()}`, {
				options: { params }
			})
			.pipe(
				map(res =>
					res.map(msg => ({
						...msg,
						type: MessageTypes.MofidMessage,
						body: changeMsgBodyStyle(msg?.body ?? ''),
						contentType: MessageContentTypes.Mofid
					}))
				),
				map(res => {
					return res;
				})
			);
	}
	getImage(imageId) {
		return this.httpService.getBlob(`${this.mtsPath + this.apiUrls.easy}media/${imageId}`, {
			responseType: 'blob'
		});
	}
	readMessages(messageIds?: string[]) {
		if (!messageIds?.length) return of(null);
		const model = { messageIds };
		if (messageIds.length > 0)
			return this.httpService.post(`${this.mtsPath + this.apiUrls.easy}textmessage/read-message`, {
				body: model
			});
		return of(null);
	}
	subscribeToLs() {
		return this.textMessages$.pipe(
			filter(msg => msg?.Body != undefined),
			map(msg => {
				if (!msg?.Body) return undefined;
				if (msg?.Type == MessageTypes.MofidMessage) return this.prepareMofidMessageDetail(msg);
				return this.prepareMessageDetail(msg);
			})
		);
	}
	createMofidUrl = () =>
		this.userDeviceService.deviceType === DeviceTypeEnum.Desktop
			? 'desktop-mofid-messages'
			: 'mobile-mofid-messages';
}
